import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { Button } from '@/ui/primitives/button';
import { Checkbox } from '@/ui/primitives/checkbox';
import { Campo, Input } from '@/ui/primitives/input';
import { Select } from '@/ui/primitives/select';
import {
  type EmpresaDetalhe,
  type ParametrosFiscais,
  parametrosFiscaisSchema,
} from '@fiscoai/contracts';

import { useDefinirParametros } from './api';
import { gravar, nulo } from './formulario';
import { SecaoClassificacao } from './secao-classificacao';

const PADRAO: ParametrosFiscais = {
  contribuinteIcms: true,
  contribuinteIpi: false,
  regimePisCofins: null,
  obrigadaEfdIcmsIpi: true,
  obrigadaEfdContribuicoes: true,
  inicioNoFiscoai: null,
};

const MARCADORES: { nome: keyof ParametrosFiscais; rotulo: string; descricao: string }[] = [
  {
    nome: 'contribuinteIcms',
    rotulo: 'Contribuinte do ICMS',
    descricao: 'Tem inscrição estadual ativa e apura ICMS.',
  },
  {
    nome: 'contribuinteIpi',
    rotulo: 'Contribuinte do IPI',
    descricao: 'Industrial ou equiparado.',
  },
  { nome: 'obrigadaEfdIcmsIpi', rotulo: 'Entrega EFD ICMS/IPI', descricao: 'SPED Fiscal.' },
  {
    nome: 'obrigadaEfdContribuicoes',
    rotulo: 'Entrega EFD-Contribuições',
    descricao: 'SPED de PIS e COFINS.',
  },
];

export function AbaParametros({
  empresa,
  podeEditar,
}: {
  empresa: EmpresaDetalhe;
  podeEditar: boolean;
}) {
  const definir = useDefinirParametros(empresa.id);
  const form = useForm<ParametrosFiscais>({
    resolver: zodResolver(parametrosFiscaisSchema),
    values: empresa.parametrosFiscais ?? PADRAO,
  });
  const enviar = form.handleSubmit((dados) =>
    gravar(() => definir.mutateAsync(dados), 'Parâmetros salvos'),
  );

  return (
    <div className="grid gap-6">
      <SecaoFormulario
        titulo="Parâmetros fiscais"
        descricao="Dizem quais créditos e obrigações se aplicam. Na dúvida, confirme com o contador."
        aoEnviar={() => void enviar()}
        acoes={
          podeEditar &&
          form.formState.isDirty && (
            <>
              <Button
                variante="fantasma"
                onClick={() => {
                  form.reset();
                }}
              >
                Descartar
              </Button>
              <Button type="submit" variante="primario" carregando={definir.isPending}>
                Salvar
              </Button>
            </>
          )
        }
      >
        <fieldset disabled={!podeEditar} className="grid gap-4">
          <legend className="sr-only">Parâmetros fiscais</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {MARCADORES.map((m) => (
              <Controller
                key={m.nome}
                control={form.control}
                name={m.nome}
                render={({ field }) => (
                  <Checkbox
                    rotulo={m.rotulo}
                    descricao={m.descricao}
                    marcado={field.value === true}
                    aoMudar={field.onChange}
                  />
                )}
              />
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Regime de PIS/COFINS">
              {(a) => (
                <Controller
                  control={form.control}
                  name="regimePisCofins"
                  render={({ field }) => (
                    <Select
                      {...a}
                      valor={field.value ?? ''}
                      aoMudar={field.onChange}
                      placeholder="Não informado"
                      opcoes={[
                        { valor: 'CUMULATIVO', rotulo: 'Cumulativo' },
                        { valor: 'NAO_CUMULATIVO', rotulo: 'Não cumulativo' },
                      ]}
                    />
                  )}
                />
              )}
            </Campo>
            <Campo
              rotulo="Notas no FiscoAI a partir de"
              ajuda="Antes disso, os documentos ficam no sistema anterior."
            >
              {(a) => <Input type="date" {...a} {...form.register('inicioNoFiscoai', nulo)} />}
            </Campo>
          </div>
        </fieldset>
      </SecaoFormulario>
      <SecaoClassificacao id={empresa.id} podeEditar={podeEditar} />
    </div>
  );
}
